import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Loader2, Search, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SparePackForm = ({ pack, vatRates, categories, onSave, onCancel, loading }) => {
    const [formData, setFormData] = useState({
        name: pack?.name || '',
        description: pack?.description || '',
        price: pack?.price || '',
        active: pack?.active ?? true,
        vat_rate_id: pack?.vat_rate_id || '',
        category_id: pack?.category_id || ''
    });

    // Set default VAT rate for new packs
    useEffect(() => {
        if (!pack && vatRates.length > 0 && !formData.vat_rate_id) {
            const standard = vatRates.find(r => r.name === 'Standard');
            if (standard) setFormData(prev => ({ ...prev, vat_rate_id: standard.id }));
        }
    }, [vatRates, pack]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name || !formData.description || !formData.price) {
            toast.error('Please fill in all fields');
            return;
        }
        onSave({ ...formData, price: parseFloat(formData.price), category_id: formData.category_id || null });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wide">Pack Name</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Filling Machine Basic Service Pack" className="h-11" required data-testid="pack-name-input" />
            </div>
            <div className="space-y-2">
                <Label htmlFor="description" className="text-xs font-bold uppercase tracking-wide">Description</Label>
                <textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Describe what's included..." className="w-full min-h-[200px] px-3 py-2 bg-background border border-input rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y" required data-testid="pack-description-input" />
            </div>
            <div className="space-y-2">
                <Label htmlFor="category" className="text-xs font-bold uppercase tracking-wide">Category</Label>
                <Select value={formData.category_id || '_none'} onValueChange={(value) => setFormData({ ...formData, category_id: value === '_none' ? '' : value })}>
                    <SelectTrigger className="h-11" data-testid="pack-category-select">
                        <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_none">Uncategorised</SelectItem>
                        {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="price" className="text-xs font-bold uppercase tracking-wide">Price Ex VAT (£)</Label>
                    <Input id="price" type="number" step="0.01" min="0" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="450.00" className="h-11" required data-testid="pack-price-input" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="vat_rate" className="text-xs font-bold uppercase tracking-wide">VAT Rate</Label>
                    <Select value={formData.vat_rate_id} onValueChange={(value) => setFormData({ ...formData, vat_rate_id: value })}>
                        <SelectTrigger className="h-11" data-testid="pack-vat-rate-select">
                            <SelectValue placeholder="Select VAT rate" />
                        </SelectTrigger>
                        <SelectContent>
                            {vatRates.map(rate => (
                                <SelectItem key={rate.id} value={rate.id}>
                                    {rate.name} ({rate.rate}%)
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="flex items-center justify-between py-2">
                <div>
                    <Label htmlFor="active" className="text-sm font-medium">Active</Label>
                    <p className="text-xs text-muted-foreground">Only active packs are shown to engineers</p>
                </div>
                <Switch id="active" checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} data-testid="pack-active-switch" />
            </div>
            <DialogFooter className="gap-2 pt-4">
                <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
                <Button type="submit" className="bg-[#7AC943] hover:bg-[#68B035] text-black font-bold" disabled={loading} data-testid="save-pack-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Pack'}
                </Button>
            </DialogFooter>
        </form>
    );
};

const SparesManagement = () => {
    const { token } = useAuth();
    const [packs, setPacks] = useState([]);
    const [vatRates, setVatRates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPack, setEditingPack] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [packToDelete, setPackToDelete] = useState(null);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [packsRes, vatRes, catRes] = await Promise.all([
                axios.get(`${API}/admin/spares`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/vat-rates`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/spares-categories`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setPacks(packsRes.data);
            setVatRates(vatRes.data);
            setCategories(catRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const getVatRateName = (vatRateId) => {
        const rate = vatRates.find(r => r.id === vatRateId);
        return rate ? `${rate.name} (${rate.rate}%)` : 'N/A';
    };

    const getCategoryName = (categoryId) => {
        if (!categoryId) return 'Uncategorised';
        const cat = categories.find(c => c.id === categoryId);
        return cat ? cat.name : 'Uncategorised';
    };

    const handleSave = async (formData) => {
        setSaving(true);
        try {
            if (editingPack) {
                await axios.put(`${API}/admin/spares/${editingPack.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Pack updated successfully');
            } else {
                await axios.post(`${API}/admin/spares`, formData, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Pack created successfully');
            }
            setDialogOpen(false);
            setEditingPack(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to save pack');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!packToDelete) return;
        setSaving(true);
        try {
            await axios.delete(`${API}/admin/spares/${packToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Pack deleted');
            setDeleteConfirmOpen(false);
            setPackToDelete(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to delete pack');
        } finally {
            setSaving(false);
        }
    };

    const filteredPacks = packs.filter(pack => {
        const matchesSearch = pack.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            pack.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || 
            (selectedCategory === 'uncategorised' ? !pack.category_id : pack.category_id === selectedCategory);
        return matchesSearch && matchesCategory;
    });

    if (loading) {
        return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>;
    }

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-heading text-brand-charcoal">Spares Packs</h1>
                    <p className="text-brand-gray-500 mt-1">Manage pricing, VAT rates and availability</p>
                </div>
                <Button onClick={() => { setEditingPack(null); setDialogOpen(true); }} className="bg-[#7AC943] hover:bg-[#68B035] text-black font-bold h-11 w-full sm:w-auto" data-testid="add-pack-btn">
                    <Plus className="h-5 w-5 mr-2" /> Add Pack
                </Button>
            </div>

            <Card className="border-0 shadow-lg">
                <CardHeader className="pb-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                        <Input type="text" placeholder="Search packs..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="h-11 pl-10" data-testid="admin-pack-search" />
                    </div>
                    {categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3" data-testid="category-filter-tabs">
                            <Button
                                size="sm"
                                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                                className={selectedCategory === 'all' ? 'bg-[#7AC943] hover:bg-[#68B035] text-black' : ''}
                                onClick={() => setSelectedCategory('all')}
                                data-testid="category-filter-all"
                            >
                                All ({packs.length})
                            </Button>
                            {categories.map(cat => {
                                const count = packs.filter(p => p.category_id === cat.id).length;
                                return (
                                    <Button
                                        key={cat.id}
                                        size="sm"
                                        variant={selectedCategory === cat.id ? 'default' : 'outline'}
                                        className={selectedCategory === cat.id ? 'bg-[#7AC943] hover:bg-[#68B035] text-black' : ''}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        data-testid={`category-filter-${cat.id}`}
                                    >
                                        {cat.name} ({count})
                                    </Button>
                                );
                            })}
                            {packs.some(p => !p.category_id) && (
                                <Button
                                    size="sm"
                                    variant={selectedCategory === 'uncategorised' ? 'default' : 'outline'}
                                    className={selectedCategory === 'uncategorised' ? 'bg-[#7AC943] hover:bg-[#68B035] text-black' : ''}
                                    onClick={() => setSelectedCategory('uncategorised')}
                                    data-testid="category-filter-uncategorised"
                                >
                                    Uncategorised ({packs.filter(p => !p.category_id).length})
                                </Button>
                            )}
                        </div>
                    )}
                </CardHeader>
                <CardContent>
                    {filteredPacks.length === 0 ? (
                        <div className="text-center py-12">
                            <Package className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" />
                            <p className="text-brand-gray-500">No packs found</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile card list */}
                            <div className="sm:hidden divide-y divide-brand-gray-200 -mx-6">
                                {filteredPacks.map(pack => (
                                    <div key={pack.id} className="px-4 py-3 cursor-pointer active:bg-brand-gray-50" onClick={() => { setEditingPack(pack); setDialogOpen(true); }} data-testid={`admin-pack-${pack.id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-brand-charcoal">{pack.name}</p>
                                                <p className="text-xs text-brand-gray-500 mt-0.5 line-clamp-1">{pack.description}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-brand-gray-100 text-brand-gray-600">
                                                        <Tag className="h-3 w-3" />{getCategoryName(pack.category_id)}
                                                    </span>
                                                </div>
                                            </div>
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${pack.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {pack.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between mt-2">
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-sm font-bold">£{pack.price.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                                                <span className="text-xs text-brand-gray-400">{getVatRateName(pack.vat_rate_id)}</span>
                                            </div>
                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <Button size="sm" variant="ghost" onClick={() => { setEditingPack(pack); setDialogOpen(true); }} className="h-8 w-8 p-0" data-testid={`edit-pack-${pack.id}`}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => { setPackToDelete(pack); setDeleteConfirmOpen(true); }} className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-pack-${pack.id}`}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop table */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Pack Name</th>
                                            <th className="hidden md:table-cell">Description</th>
                                            <th className="hidden lg:table-cell">Category</th>
                                            <th className="text-right">Price (Ex VAT)</th>
                                            <th className="hidden md:table-cell text-center">VAT</th>
                                            <th className="text-center">Status</th>
                                            <th className="text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPacks.map(pack => (
                                            <tr key={pack.id} className="cursor-pointer hover:bg-brand-gray-50" onClick={() => { setEditingPack(pack); setDialogOpen(true); }} data-testid={`admin-pack-desktop-${pack.id}`}>
                                                <td className="font-medium">{pack.name}</td>
                                                <td className="hidden md:table-cell max-w-[300px] truncate text-brand-gray-500">{pack.description}</td>
                                                <td className="hidden lg:table-cell">
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-brand-gray-100 text-brand-gray-600">
                                                        <Tag className="h-3 w-3" />{getCategoryName(pack.category_id)}
                                                    </span>
                                                </td>
                                                <td className="text-right font-bold whitespace-nowrap">£{pack.price.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                                                <td className="hidden md:table-cell text-center text-sm text-brand-gray-500">{getVatRateName(pack.vat_rate_id)}</td>
                                                <td className="text-center">
                                                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${pack.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                        {pack.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button size="sm" variant="ghost" onClick={() => { setEditingPack(pack); setDialogOpen(true); }} className="h-8 w-8 p-0" data-testid={`edit-pack-${pack.id}`}>
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => { setPackToDelete(pack); setDeleteConfirmOpen(true); }} className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-pack-${pack.id}`}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editingPack ? 'Edit Spares Pack' : 'Add Spares Pack'}</DialogTitle>
                        <DialogDescription>{editingPack ? 'Update the pack details.' : 'Create a new spares pack for engineers to quote.'}</DialogDescription>
                    </DialogHeader>
                    <SparePackForm pack={editingPack} vatRates={vatRates} categories={categories} onSave={handleSave} onCancel={() => setDialogOpen(false)} loading={saving} />
                </DialogContent>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-red-600">Delete Pack</DialogTitle>
                        <DialogDescription>Are you sure you want to delete "{packToDelete?.name}"? This action cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SparesManagement;
